import lod_ from "lodash";
import {
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemText
} from "@mui/material";
import { useEffect, useState } from "react";
import FormAction from "redux-react/actions/formAction";
import { useDispatch } from "react-redux";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { DateTime } from "luxon";
import MDButton from "components/Basics/MDButton";
import { t } from "i18next";
import Languages from "flatpickr/dist/l10n/index";
import MDDatePicker from "components/Basics/MDDatePicker";
import i18n from "i18n";

/**
 * Dialog to launch the scrap of reviews for a given resource
 * Allows to choose the date from which we want to scrap reviews
 * @param {boolean} open - If the dialog is open or not
 * @param {function} onClose - Function to call to close the dialog
 * @param {function} perform - Function to call to perform the action
 * @param {object} resource - The resource on which we want to scrap reviews
 * @param {object} action - The action to perform
 * @param {object|array} values - The values on which we want to perform the action
 * @returns {JSX.Element} - The ChangeDateCampaign component
 */
const ChangeDateCampaign = ({ open, onClose, perform, resource, action, values }) => {
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const DefaultLanguage = Languages[i18n.language] || Languages.en;
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [checkState, setCheckState] = useState(false);

	const [numberOfPhone, setNumberOfPhone] = useState(null);
	const [numberOfEmail, setNumberOfEmail] = useState(null);
	const [numberOfContact, setNumberOfContact] = useState(null);

	const [listOfCapaign, setListOfCapaign] = useState([]);

	const handleClose = () => {
		setListOfCapaign([]);
		setEndDate(null);
		setStartDate(null);
		onClose();
	};

	const handleCheckState = campaings => {
		const uniqueStates = campaings.every(item => item.state === "created");

		if (!uniqueStates) {
			setCheckState(false);
		} else {
			setCheckState(true);
		}

		const nbrPhone = lod_.sumBy(
			campaings,
			item => lod_.filter(item.contacts ?? [], c => c?.phone && c.phone.trim() !== "").length
		);
		const nbrEmail = lod_.sumBy(
			campaings,
			item => lod_.filter(item.contacts, c => c?.email && c.email.trim() !== "").length
		);
		const nbrContracts = lod_.sumBy(campaings ?? [], "numberOfContract");

		setNumberOfPhone(nbrPhone ?? null);
		setNumberOfEmail(nbrEmail ?? null);
		setNumberOfContact(nbrContracts ?? null);
	};

	const stateColors = {
		created: "#5cb85c",
		canceled: "#d9534f"
	};

	useEffect(() => {
		if (open === true && resource?.storage?.collection && values?.id_coupure) {
			setLoading(true);
			dispatch(
				FormAction.getItemsFromCollection(
					resource?.storage?.collection,
					{
						query: { id_coupure: values.id_coupure },
						projection: {
							_id: 0,
							state: 1,
							nom_voie: 1,
							numero: 1,
							commune: 1,
							code_postal: 1,
							startDate: 1,
							endDate: 1,
							cause: 1,
							contacts: 1,
							numberOfContract: 1
						}
					},
					res => {
						if (!lod_.isNil(res.items) && !lod_.isEmpty(res.items)) {
							setListOfCapaign(res.items);
							setLoading(false);
							handleCheckState(res.items);
							const campaign = res.items[0];
							if (!lod_.isNil(campaign?.startDate)) {
								const dateSta = DateTime.fromISO(campaign.startDate).toJSDate();

								setStartDate(dateSta);
							} else {
								setStartDate(null);
							}

							if (!lod_.isNil(campaign?.endDate)) {
								setEndDate(DateTime.fromISO(campaign.endDate).toJSDate());
							} else {
								setEndDate(null);
							}
						} else {
							setListOfCapaign([]);
							setCheckState(false);
							setLoading(false);
						}
					}
				)
			);
		} else {
			setListOfCapaign([]);
			setCheckState(false);
			setStartDate(null);
			setEndDate(null);
		}
	}, [open]);

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{t("FORMS.RESOURCE.campaignTitle")}</DialogTitle>
			<DialogContent>
				{loading && (
					<MDBox display="flex" justifyContent="center" alignItems="center">
						<CircularProgress color="info" size={24} />
					</MDBox>
				)}

				{!loading && checkState && (
					<>
						<MDBox mb={2}>
							{values?.cause && (
								<MDBox display="flex">
									<MDBox mr={1}>{t("FORMS.RESOURCE.campaignCause")}</MDBox>
									<MDTypography variant="subtitle2" color="text">
										{values?.cause}
									</MDTypography>
								</MDBox>
							)}
							{!lod_.isNil(listOfCapaign) && (
								<MDBox display="flex">
									<MDBox mr={1}>{t("FORMS.RESOURCE.campaignNumberAdress")}</MDBox>
									<MDTypography variant="subtitle2" color="text">
										{listOfCapaign?.length}
									</MDTypography>
								</MDBox>
							)}
							{!lod_.isNil(numberOfPhone) && (
								<MDBox display="flex">
									<MDBox mr={1}>{t("FORMS.RESOURCE.campaignNumberPhone")}</MDBox>
									<MDTypography variant="subtitle2" color="text">
										{numberOfPhone}
									</MDTypography>
								</MDBox>
							)}
							{!lod_.isNil(numberOfEmail) && (
								<MDBox display="flex">
									<MDBox mr={1}>{t("FORMS.RESOURCE.campaignNumberEmail")}</MDBox>
									<MDTypography variant="subtitle2" color="text">
										{numberOfEmail}
									</MDTypography>
								</MDBox>
							)}
							{!lod_.isNil(numberOfContact) && (
								<MDBox display="flex">
									<MDBox mr={1}>{t("FORMS.RESOURCE.campaignNumberContact")}</MDBox>
									<MDTypography variant="subtitle2" color="text">
										{numberOfContact}
									</MDTypography>
								</MDBox>
							)}
						</MDBox>
						<MDBox>{t("FORMS.RESOURCE.campaignDescription")}</MDBox>
						<MDBox mt={1} mb={2}>
							<MDBox mb={1} className="datePickerContainer">
								<MDTypography variant="subtitle2" color="text">
									{t("FORMS.RESOURCE.startingDate")}
								</MDTypography>
								<MDDatePicker
									type="date"
									label={t("FORMS.RESOURCE.startingDate")}
									onChange={(dateObj, dateStr, instance) => {
										if (dateObj[0] >= endDate) {
											setEndDate(null);
										}
										if (DateTime.now().plus({ minutes: 30 }).toJSDate() > dateObj[0]) {
											setStartDate(
												DateTime.now()
													.plus({
														minutes: 31
													})
													.toJSDate()
											);
										} else {
											setStartDate(dateObj[0]);
										}
									}}
									value={startDate}
									options={{
										enableTime: true,
										dateFormat: "d M Y H:i",
										minDate: DateTime.now().plus({ minutes: 30 }).toJSDate(),
										locale: {
											...DefaultLanguage,
											firstDayOfWeek: 1
										}
									}}
									input={{
										placeholder: t("FORMS.RESOURCE.startingDate"),
										style: {
											width: "100%"
										}
									}}
								/>
							</MDBox>

							<MDBox>
								<MDTypography variant="subtitle2" color="text">
									{t("FORMS.RESOURCE.endingDate")}
								</MDTypography>
								<MDDatePicker
									type="date"
									label={t("FORMS.RESOURCE.endingDate")}
									onChange={(dateObj, dateStr, instance) => {
										if (dateObj[0] < startDate) {
											setStartDate(null);
										}
										if (
											lod_.isNil(startDate) &&
											DateTime.now().plus({ minutes: 30 }).toJSDate() > dateObj[0]
										) {
											setEndDate(DateTime.fromJSDate(dateObj[0]).plus({ minutes: 1 }).toJSDate());
										} else {
											setEndDate(dateObj[0]);
										}
									}}
									options={{
										time_24hr: true,
										enableTime: true,
										dateFormat: "d M Y H:i",
										minDate: !lod_.isNil(startDate)
											? startDate
											: DateTime.now().plus({ minutes: 30 }).toJSDate(),
										locale: {
											...DefaultLanguage,
											firstDayOfWeek: 1
										},
										defaultDate: endDate
									}}
									input={{
										placeholder: t("FORMS.RESOURCE.endingDate"),
										style: {
											width: "100%"
										}
									}}
								/>
							</MDBox>
						</MDBox>
						<MDBox>{t("FORMS.RESOURCE.campaignSelectionInfo")}</MDBox>
						<List sx={{ width: "100%", bgcolor: "background.paper" }}>
							{listOfCapaign.map(value => (
								<ListItem key={value} disableGutters>
									<ListItemText
										secondary={
											<>
												<Chip
													size="small"
													variant="outlined"
													style={{ backgroundColor: stateColors[value?.state] || "#191919" }}
													label={t(`FORMS.RESOURCE.${value?.state}`)}
												/>
												{[value?.numero, value?.nom_voie, value?.commune, value?.code_postal]
													.filter(Boolean)
													.join(" ")}
											</>
										}
									/>
								</ListItem>
							))}
						</List>
					</>
				)}

				{!checkState && (
					<MDTypography variant="subtitle2" color="text">
						{t("FORMS.RESOURCE.campaignError")}
					</MDTypography>
				)}
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={handleClose}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton
					variant="contained"
					color="info"
					disabled={!checkState || lod_.isNil(startDate) || lod_.isNil(endDate)}
					onClick={() => {
						const copyAction = lod_.cloneDeep(action);
						lod_.set(copyAction, "config.startDate", startDate);
						lod_.set(copyAction, "config.endDate", endDate);
						lod_.set(copyAction, "config.id_coupure", values?.id_coupure);

						if (!Array.isArray(values)) {
							values = [values];
						}
						perform(copyAction, values);
						onClose();
					}}
				>
					{t("SETTINGS.confirm")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default ChangeDateCampaign;
